import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, AlertTriangle } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/billing')({
  staticData: { crumb: 'Subskrypcja' },
  component: BillingPage,
});

interface SubResponse {
  tenant: { tier: string; status: string };
  subscription: {
    tier: string;
    interval: 'month' | 'year';
    state: string;
    current_period_end: number | null;
    cancel_at_period_end: boolean;
  } | null;
}

interface UsageResponse {
  tier: {
    id: string;
    name: string;
    max_pages: number;
    max_media_items: number;
    max_media_mb: number;
    max_agent_rounds: number;
  };
  usage: {
    pages: number;
    media_items: number;
    media_mb: number;
    agent_rounds: number;
  };
}

const TIER_LABEL: Record<string, string> = {
  preview: 'Preview (brak płatności)',
  pozyskaj: 'Pozyskaj',
  // legacy
  start: 'Pozyskaj (legacy: Start)',
  pro: 'Pozyskaj (legacy: Pro)',
  pro_bundle: 'Pozyskaj (legacy: Pro Bundle)',
  multi: 'Pozyskaj (legacy: Multi)',
};

function BillingPage() {
  const sub = useQuery({
    queryKey: ['admin', 'subscription'],
    queryFn: async (): Promise<SubResponse> => {
      const res = await api.api.admin.subscription.$get();
      if (!res.ok) throw new Error('failed_to_load_subscription');
      return (await res.json()) as SubResponse;
    },
    refetchOnWindowFocus: true,
  });

  const usage = useQuery({
    queryKey: ['admin', 'usage'],
    queryFn: async (): Promise<UsageResponse> => {
      const res = await api.api.admin.usage.$get();
      if (!res.ok) throw new Error('failed_to_load_usage');
      return (await res.json()) as UsageResponse;
    },
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const subscription = sub.data?.subscription;
  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end * 1000).toLocaleDateString('pl-PL')
    : null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Subskrypcja
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Aktualny plan, faktury i zarządzanie subskrypcją w portalu Stripe.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Aktualny plan</CardTitle>
          <CardDescription>
            Wszystkie zmiany planu i metody płatności odbywają się w portalu klienta Stripe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {sub.isPending ? (
            <p className="text-sm text-text-muted">Ładowanie…</p>
          ) : sub.isError ? (
            <p className="rounded-md bg-error/10 p-3 text-sm text-error">
              Nie udało się pobrać danych. Odśwież stronę.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              <Metric label="Plan" value={TIER_LABEL[sub.data!.tenant.tier] ?? sub.data!.tenant.tier} />
              <Metric label="Status" value={sub.data!.tenant.status} />
              <Metric
                label="Następne odnowienie"
                value={
                  periodEnd
                    ? subscription?.cancel_at_period_end
                      ? `Wygasa ${periodEnd}`
                      : periodEnd
                    : '—'
                }
              />
            </div>
          )}

          {subscription ? (
            <p className="text-xs text-text-subtle">
              Subskrypcja Stripe: <span className="font-mono">{subscription.tier}</span> ·{' '}
              {subscription.interval === 'month' ? 'miesięczna' : 'roczna'} · stan:{' '}
              <span className="font-mono">{subscription.state}</span>
            </p>
          ) : (
            <p className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
              Brak aktywnej subskrypcji. Po uruchomieniu strony otrzymasz link do
              Stripe Checkout od operatora.
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="secondary"
              disabled={!subscription}
              onClick={async () => {
                const res = await fetch('/api/admin/billing/portal', {
                  method: 'POST',
                  credentials: 'include',
                });
                if (res.ok) {
                  const { url } = (await res.json()) as { url: string };
                  window.location.href = url;
                } else {
                  const j = (await res.json().catch(() => ({}))) as { error?: string };
                  alert(
                    j.error === 'forbidden_role'
                      ? 'Tylko właściciel kliniki może zarządzać subskrypcją.'
                      : j.error ?? 'Nie udało się otworzyć portalu.'
                  );
                }
              }}
            >
              <ExternalLink className="size-4" aria-hidden="true" /> Otwórz portal Stripe
            </Button>
          </div>
        </CardContent>
      </Card>

      {usage.data ? <UsageCard data={usage.data} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>Plan Pozyskaj</CardTitle>
          <CardDescription>
            Jeden plan, wszystkie podstawowe funkcje. Dodatkowe usługi
            (content marketplace, backlinki, video, AI-notatki, NFZ, telepotwierdzenia)
            sprzedajemy à la carte na życzenie.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md bg-surface p-4 ring-1 ring-black/5">
              <p className="text-xs uppercase tracking-wide text-text-subtle">Miesięcznie</p>
              <p className="mt-1 font-display text-3xl font-semibold text-text">99 zł</p>
              <p className="text-xs text-text-muted">brutto / mies. + VAT</p>
            </div>
            <div className="rounded-md bg-accent/5 p-4 ring-1 ring-accent/20">
              <p className="text-xs uppercase tracking-wide text-accent">
                Rocznie · oszczędzasz 2 mies.
              </p>
              <p className="mt-1 font-display text-3xl font-semibold text-text">999 zł</p>
              <p className="text-xs text-text-muted">brutto / rok + VAT</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-text-muted">
            Bez prowizji od wizyt. Bez ukrytych opłat. Pełna lista funkcji w naszym{' '}
            <a
              href="https://pozyskajpacjenta.pl/cennik"
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline"
            >
              cenniku
            </a>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-surface p-4 ring-1 ring-black/5">
      <p className="text-xs uppercase tracking-wide text-text-subtle">{label}</p>
      <p className="mt-1 font-display text-base font-semibold text-text">{value}</p>
    </div>
  );
}

function UsageCard({ data }: { data: UsageResponse }) {
  const bars: Array<{
    label: string;
    used: number;
    limit: number;
    unit: string;
  }> = [
    { label: 'Podstrony', used: data.usage.pages, limit: data.tier.max_pages, unit: '' },
    {
      label: 'Pliki w bibliotece',
      used: data.usage.media_items,
      limit: data.tier.max_media_items,
      unit: '',
    },
    {
      label: 'Przestrzeń mediów',
      used: data.usage.media_mb,
      limit: data.tier.max_media_mb,
      unit: ' MB',
    },
    {
      label: 'Rundy agenta e-mail',
      used: data.usage.agent_rounds,
      limit: data.tier.max_agent_rounds,
      unit: '',
    },
  ];

  const approaching = bars.some(
    (b) => b.limit > 0 && b.used / b.limit >= 0.9 && b.used < b.limit
  );
  const reached = bars.some((b) => b.limit > 0 && b.used >= b.limit);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Wykorzystanie planu</CardTitle>
        <CardDescription>
          Limity dla planu <span className="font-mono">{data.tier.name}</span>. Po
          przekroczeniu danego limitu odpowiednie funkcje wyświetlają komunikat o
          podniesieniu planu.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {reached ? (
          <p className="flex items-start gap-2 rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20">
            <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
            Osiągnięto co najmniej jeden limit. Podnieś plan, aby kontynuować dodawanie
            zawartości.
          </p>
        ) : approaching ? (
          <p className="flex items-start gap-2 rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
            <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
            Zbliżasz się do limitu. Rozważ wcześniejsze przejście na wyższy plan.
          </p>
        ) : null}

        <ul role="list" className="space-y-3">
          {bars.map((b) => (
            <UsageBar key={b.label} {...b} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function UsageBar({
  label,
  used,
  limit,
  unit,
}: {
  label: string;
  used: number;
  limit: number;
  unit: string;
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color =
    pct >= 100 ? 'bg-error' : pct >= 90 ? 'bg-warning' : 'bg-accent';
  return (
    <li>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-text-muted">{label}</span>
        <span className="tabular-nums text-text">
          {used}
          {unit} <span className="text-text-subtle">/ {limit}{unit}</span>
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface ring-1 ring-black/5">
        <div
          className={cn('h-full rounded-full transition-all', color)}
          style={{ width: `${pct}%` }}
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          role="progressbar"
          aria-label={label}
        />
      </div>
    </li>
  );
}
